import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';

type Role = 'MANAGER' | 'CLERK';

interface Clerk {
  clerkId: string;
  name: string;
  email: string;
  role: Role;
  linked: boolean;
}

// createApiRequest はHTTPエラー時にレスポンス本文(JSON文字列)を Error.message に入れる。
// BE は {status,message,timestamp} を返すため、message を取り出して表示する。
const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

/**
 * 店員管理セクション（招待・ロール変更・削除）。要件定義 0001 §6/§7 / feast-fe ADR 0009。
 * BE: GET/POST /stores/{storeId}/clerks, PATCH /stores/{storeId}/clerks/{clerkId}/role,
 *     DELETE /stores/{storeId}/clerks/{clerkId}。
 * 招待中の店員を削除すると BE 側で招待が無効化される。最後の管理者保護などの不変条件は
 * BE が強制し、FE は 4xx の message を表示する。
 */
const ClerkSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';
  const ownClerkId = localStorage.getItem('clerk_id');
  const theme = useTheme();
  // 狭幅（スマホ）でのみ操作を下段へ寄せる。それ以外は従来どおり右端インライン。
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const [clerks, setClerks] = useState<Clerk[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState<Clerk | null>(null);
  const [nameTarget, setNameTarget] = useState<Clerk | null>(null);
  const [nameValue, setNameValue] = useState('');

  // 管理者は全員、CLERK は自分自身のみ名前を変更できる。
  const canEditName = (c: Clerk) => isManager || c.clerkId === ownClerkId;

  const fetchClerks = useCallback(() => {
    if (!store_id) return;
    setLoading(true);
    createApiRequest<Clerk[]>(`/stores/${store_id}/clerks`, { method: 'GET' })
      .then((data) => setClerks(data || []))
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setLoading(false));
  }, [store_id]);

  useEffect(() => {
    fetchClerks();
  }, [fetchClerks]);

  const invite = () => {
    if (!store_id) return;
    if (!email.trim() || !name.trim()) {
      setError('メールアドレスと名前を入力してください。');
      return;
    }
    setError(null);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/clerks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), name: name.trim() }),
    })
      .then(() => {
        setEmail('');
        setName('');
        fetchClerks();
      })
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setBusy(false));
  };

  const changeRole = (clerk: Clerk, next: Role) => {
    if (!store_id) return;
    if (next === clerk.role) return; // 変更なしは何もしない
    setError(null);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/clerks/${clerk.clerkId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: next }),
    })
      .then(() => fetchClerks())
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setBusy(false));
  };

  const openNameEdit = (clerk: Clerk) => {
    setNameValue(clerk.name);
    setNameTarget(clerk);
  };

  const saveName = () => {
    if (!store_id || !nameTarget) return;
    const v = nameValue.trim();
    if (!v) {
      setError('名前を入力してください。');
      return;
    }
    setError(null);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/clerks/${nameTarget.clerkId}/name`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: v }),
    })
      .then(() => fetchClerks())
      .catch((e) => setError(extractMessage(e)))
      .finally(() => {
        setBusy(false);
        setNameTarget(null);
      });
  };

  const removeClerk = (clerk: Clerk) => {
    if (!store_id) return;
    setError(null);
    setBusy(true);
    createApiRequest(`/stores/${store_id}/clerks/${clerk.clerkId}`, {
      method: 'DELETE',
    })
      .then(() => fetchClerks())
      .catch((e) => setError(extractMessage(e)))
      .finally(() => {
        setBusy(false);
        setTarget(null);
      });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        店員管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isManager && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            店員を招待
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>
            招待先は <strong>Google または Apple でログインできるメールアドレス</strong> である必要があります。
            FEAST はパスワードを使わず Google・Apple のログインだけで利用するため、どちらでもログインできないアドレスでは初回ログイン・連携ができません。
          </Alert>
          <Typography variant="caption" color="text.secondary">
            招待された方がこのアドレスで Google または Apple のいずれかで初回ログインすると、店員（CLERK）として有効化されます。
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
            <TextField label="メールアドレス" value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth type="email" inputProps={{ maxLength: 254 }} />
            <TextField label="名前" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth inputProps={{ maxLength: 50 }} />
            <Button variant="contained" onClick={invite} disabled={busy} sx={{ whiteSpace: 'nowrap' }}>
              招待
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List disablePadding>
            {clerks.map((c, i) => (
              <React.Fragment key={c.clerkId}>
                {i > 0 && <Divider component="li" />}
                {(() => {
                  // 操作（名前変更・ロール変更・削除）。狭幅では名前の下段、広幅では右端インライン。
                  const controls = canEditName(c) || isManager ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      {canEditName(c) && (
                        <IconButton size="small" disabled={busy} aria-label="名前を変更" onClick={() => openNameEdit(c)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                      {isManager && (
                        <FormControl size="small">
                          <Select
                            value={c.role}
                            // 招待中（未連携）の店員は管理者にできないため、連携前はロール変更不可。
                            disabled={busy || !c.linked}
                            title={!c.linked ? '招待中の店員は初回ログイン後にロール変更できます' : undefined}
                            onChange={(e: SelectChangeEvent) => changeRole(c, e.target.value as Role)}
                            sx={{ minWidth: 104 }}
                          >
                            <MenuItem value="MANAGER">管理者</MenuItem>
                            <MenuItem value="CLERK">店員</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      {isManager && (
                        <IconButton size="small" color="error" disabled={busy} aria-label="削除" onClick={() => setTarget(c)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  ) : null;
                  return (
                    <ListItem
                      alignItems="flex-start"
                      sx={compact ? { display: 'block' } : undefined}
                      secondaryAction={!compact ? controls : undefined}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <span>{c.name}</span>
                            <Chip size="small" color={c.role === 'MANAGER' ? 'primary' : 'default'} label={c.role === 'MANAGER' ? '管理者' : '店員'} />
                            <Chip size="small" variant="outlined" color={c.linked ? 'success' : 'warning'} label={c.linked ? '連携済み' : '招待中'} />
                          </Box>
                        }
                        secondary={c.email}
                      />
                      {compact && controls && <Box sx={{ mt: 1 }}>{controls}</Box>}
                    </ListItem>
                  );
                })()}
              </React.Fragment>
            ))}
            {clerks.length === 0 && (
              <ListItem>
                <ListItemText primary="店員がいません。" />
              </ListItem>
            )}
          </List>
        )}
      </Paper>

      {!isManager && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          店員の招待・ロール変更・削除は管理者のみ可能です。自分の名前は変更できます。
        </Typography>
      )}

      <Dialog open={nameTarget !== null} onClose={() => setNameTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>名前の変更</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="名前"
            fullWidth
            size="small"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            inputProps={{ maxLength: 50 }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setNameTarget(null)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={saveName} disabled={busy || !nameValue.trim()}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={target !== null}
        onClose={() => setTarget(null)}
        onConfirm={() => target && removeClerk(target)}
        title="店員を削除"
        message={
          target
            ? target.linked
              ? `${target.name} を削除します。よろしいですか？`
              : `招待中の ${target.name}（${target.email}）を削除します。この招待は無効になります。よろしいですか？`
            : ''
        }
        confirmText="削除"
        confirmColor="error"
        closeColor="inherit"
      />
    </Box>
  );
};

export default ClerkSection;
